import { useMemo, useState } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import { Input } from '../ui/input';
import { SimpleTable } from './K2Tables';
import { K2ItemDetail } from './K2ItemDetail';
import { aggregateItems, fmtQty } from './itemsUtils';
import { fmtKc } from './k2utils';

const METRICS = [{ id: 'value', label: 'Hodnota Kč' }, { id: 'qty', label: 'Množství' }];

const Pct = ({ v }) => (v == null ? <span className="text-[#94A3B8]">—</span> : (
  <span className={v > 0 ? 'text-[#DC2626]' : 'text-[#16A34A]'}>{v >= 0 ? '+' : ''}{v.toFixed(1)} %</span>
));

function columns(metric, withPrev) {
  const q = metric === 'qty';
  const f = (qk, vk, cls) => ({ num: true, className: cls, render: (r) => (q ? fmtQty(r[qk], r.unit) : fmtKc(r[vk])) });
  const cols = [
    { key: 'rank', label: '#', render: (r) => r.rank, className: 'text-[#94A3B8] tabular-nums' },
    { key: 'code', label: 'Kód', className: 'whitespace-nowrap' },
    { key: 'name', label: 'Zboží', className: 'font-medium' },
    { key: 'w', label: 'Odepsáno', ...f('wq', 'wv', 'font-semibold text-[#DC2626]') },
    { key: 's', label: 'Přebytek', ...f('sq', 'sv', 'text-[#16A34A]') },
    { key: 'n', label: 'Čistě', ...f('nq', 'nv', 'font-semibold') },
    { key: 'b290', label: 'Řada 290', ...f('q290', 'v290') },
    { key: 'b291', label: 'Řada 291', ...f('q291', 'v291') },
  ];
  if (withPrev) {
    cols.push({ key: 'prev', label: 'Předch. čistě', ...f('prevQ', 'prevV', 'text-[#57534E]') });
    cols.push({ key: 'delta', label: 'Změna', num: true, render: (r) => <Pct v={q ? r.deltaQ : r.deltaV} /> });
  }
  cols.push({ key: 'docCount', label: 'Dokladů', num: true });
  return cols;
}

const TopChart = ({ rows, metric }) => {
  const q = metric === 'qty';
  const data = rows.slice(0, 10).map((r) => ({
    label: r.name.length > 38 ? `${r.name.slice(0, 38)}…` : r.name,
    w290: q ? r.wq290 : r.wv290,
    w291: q ? r.wq291 : r.wv291,
    unit: r.unit,
  }));
  return (
    <div className="h-[380px]" data-testid="k2-items-top-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
          <XAxis type="number" tick={{ fontSize: 11, fill: '#57534E' }} />
          <YAxis type="category" dataKey="label" width={280} tick={{ fontSize: 11, fill: '#1C1917' }} />
          <Tooltip formatter={(v, n, p) => (q ? fmtQty(v, p.payload.unit) : fmtKc(v))} />
          <Legend />
          <Bar dataKey="w290" name="Řada 290" stackId="w" fill="#0EA5E9" />
          <Bar dataKey="w291" name="Řada 291" stackId="w" fill="#8B5CF6" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export const K2ItemsSection = ({ items, loading, error, range, withPrev }) => {
  const [metric, setMetric] = useState('value');
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState(null);
  const all = useMemo(() => aggregateItems(items, range, withPrev), [items, range, withPrev]);
  const rows = useMemo(() => {
    const k = metric === 'qty' ? 'wq' : 'wv';
    const ql = q.toLowerCase();
    return [...all].sort((a, b) => b[k] - a[k]).map((r, i) => ({ ...r, rank: i + 1 }))
      .filter((r) => !ql || `${r.code} ${r.name}`.toLowerCase().includes(ql));
  }, [all, metric, q]);

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 shadow-sm space-y-4" data-testid="k2-items-section">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-base md:text-lg font-bold text-[#052E16] font-['Manrope'] flex items-center gap-2">
            Nejodepisovanější položky {loading && <Loader2 className="w-4 h-4 animate-spin text-[#16A34A]" data-testid="k2-items-loading" />}
          </h2>
          <p className="text-xs text-[#57534E]">{all.length} druhů zboží · seřazeno podle odepsaného {metric === 'qty' ? 'množství' : 'objemu v Kč'} · kliknutím zobrazíte doklady</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex gap-1 p-1 bg-[#F1F5F1] rounded-xl">
            {METRICS.map((m) => (
              <button key={m.id} type="button" onClick={() => setMetric(m.id)} data-testid={`k2-items-metric-${m.id}`}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${metric === m.id ? 'bg-white text-[#16A34A] shadow-sm' : 'text-[#57534E] hover:text-[#16A34A]'}`}>
                {m.label}
              </button>
            ))}
          </div>
          <div className="relative w-64">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat zboží…" className="pl-9" data-testid="k2-items-search" />
          </div>
        </div>
      </div>
      {error && <div className="p-3 rounded-xl bg-[#FEE2E2] text-[#991B1B] text-sm" data-testid="k2-items-error">{error}</div>}
      <div className={`space-y-4 transition-opacity ${loading ? 'opacity-50' : ''}`}>
        {rows.length > 0 && !q && <TopChart rows={rows} metric={metric} />}
        {metric === 'qty' && <p className="text-xs text-[#94A3B8]">Množství je v jednotce daného zboží (Ks, Kg, l…), žebříček podle množství proto porovnává různé jednotky.</p>}
        <SimpleTable key={`${metric}-${q}`} testId="k2-items-table" columns={columns(metric, withPrev)} rows={rows} rowKey={(r) => r.code || r.name} onRowClick={setDetail} />
      </div>
      <K2ItemDetail article={detail} onClose={() => setDetail(null)} />
    </div>
  );
};
